import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';
const CONTACT_EMAIL = 'contact@criptonews.site';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, subject, message } = formData;
    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      `Nome: ${name}\nE-mail: ${email}\n\n${message}`
    )}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>Contato | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Entre em contato com a equipe do ${SITE_NAME}. Aceitamos dúvidas, sugestões, correções e propostas de parceria.`}
        />
        <link rel="canonical" href={`${SITE_URL}/contact`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          <div className="bg-white rounded-lg shadow p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Entre em Contato</h1>
            <p className="text-gray-600 mb-8">
              Tem alguma dúvida, encontrou um erro ou quer fazer uma parceria? Adoramos receber seu contato.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div className="flex flex-col items-center text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl mb-2">✉️</div>
                <h3 className="font-semibold text-gray-900 mb-1">E-mail</h3>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-blue-600 hover:underline text-sm break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl mb-2">📰</div>
                <h3 className="font-semibold text-gray-900 mb-1">Editorial</h3>
                <p className="text-gray-600 text-sm">
                  Dicas de notícias, correções e sugestões de conteúdo
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl mb-2">🤝</div>
                <h3 className="font-semibold text-gray-900 mb-1">Parcerias</h3>
                <p className="text-gray-600 text-sm">
                  Publicidade e oportunidades de negócio
                </p>
              </div>
            </div>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h2 className="text-xl font-bold text-green-800 mb-2">Mensagem pronta para envio!</h2>
                <p className="text-green-700">
                  Seu cliente de e-mail deve ter aberto. Caso não tenha, envie diretamente para{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Seu Nome <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="João Silva"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Seu E-mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="joao@exemplo.com"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Assunto <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Selecione um assunto</option>
                    <option value="Dúvida Geral">Dúvida Geral</option>
                    <option value="Correção de Conteúdo">Correção de Conteúdo</option>
                    <option value="Parceria / Publicidade">Parceria / Publicidade</option>
                    <option value="Problema Técnico">Problema Técnico</option>
                    <option value="Privacidade / Dados Pessoais">Privacidade / Dados Pessoais</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Mensagem <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Escreva sua mensagem aqui..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition"
                >
                  Enviar Mensagem
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Ao clicar em "Enviar Mensagem", seu cliente de e-mail será aberto. Respondemos em até 2 dias úteis.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
